import { useState, useEffect } from 'react';
import { getCreditBalance, getServiceCosts } from '../../services/api';
import SessionManager from '../../utils/sessionManager';
import InsufficientCreditsModal from './InsufficientCreditsModal';

/**
 * CreditCostBadge - Shows credit cost for a service and user's balance
 * 
 * @param {string} serviceType - 'resume_enhance', 'job_apply', 'ai_trainer', 'ai_live_trainer'
 * @param {string} serviceName - Display name for the service
 * @param {boolean} showBalance - Whether to show current balance (default: true)
 * @param {string} size - 'sm', 'md', 'lg' (default: 'md')
 */
const CreditCostBadge = ({ 
  serviceType, 
  serviceName,
  showBalance = true,
  size = 'md',
  className = ''
}) => {
  const [balance, setBalance] = useState(null);
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const session = SessionManager.getSession('candidate');
        if (!session?.profile?.id) {
          setLoading(false);
          return;
        }

        // Fetch balance and costs in parallel
        const [balanceData, costsData] = await Promise.all([
          getCreditBalance(session.profile.id),
          getServiceCosts()
        ]);

        setBalance(balanceData);
        setCost(costsData[serviceType] || 0);
      } catch (error) {
        console.error('Error fetching credit data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Listen for credit updates
    const handleCreditUpdate = () => fetchData();
    window.addEventListener('creditBalanceUpdated', handleCreditUpdate);
    return () => window.removeEventListener('creditBalanceUpdated', handleCreditUpdate);
  }, [serviceType]);

  const hasEnoughCredits = balance && cost !== null && balance.balance >= cost;
  const isLowBalance = balance && balance.balance <= 30;

  const handleCreditsAdded = (newBalance) => {
    setBalance(prev => ({ ...prev, balance: newBalance }));
    window.dispatchEvent(new CustomEvent('creditBalanceUpdated'));
  };

  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };

  if (loading) {
    return (
      <div className={`inline-flex items-center gap-2 bg-gray-100 rounded-full animate-pulse ${sizeClasses[size]} ${className}`}>
        <div className="w-12 h-4 bg-gray-300 rounded"></div>
      </div>
    );
  }

  if (balance === null || cost === null) {
    return null;
  }

  return (
    <>
      <div className={`inline-flex items-center gap-3 ${className}`}>
        {/* Cost Badge */}
        <div className={`inline-flex items-center gap-1.5 rounded-full font-medium ${sizeClasses[size]} ${
          hasEnoughCredits 
            ? 'bg-purple-100 text-purple-700 border border-purple-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" />
          </svg>
          <span>{cost} credits</span>
        </div>

        {/* Balance Display */}
        {showBalance && (
          <div className={`inline-flex items-center gap-1.5 rounded-full ${sizeClasses[size]} ${
            isLowBalance 
              ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' 
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            <span>Balance:</span>
            <span className="font-semibold">{balance.balance}</span>
          </div>
        )}

        {/* Warning if insufficient */}
        {!hasEnoughCredits && (
          <button
            onClick={() => setShowModal(true)}
            className={`inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-medium ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Get Credits
          </button>
        )}
      </div>

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        requiredCredits={cost}
        availableCredits={balance?.balance || 0}
        serviceName={serviceName}
        onCreditsAdded={handleCreditsAdded}
      />
    </>
  );
};

export default CreditCostBadge;
